<template>
    <div class="cz2__steps-option cz2__steps-options__shared">
        <step-header
            :hasSubMenu="selectedComponentHasSubMenu"
            :subMenuTitle="subMenuTitle"
            :subMenuSubTitle="subMenuSubTitle"
            :showSubMenuTitle="displaySubMenuTitle"
            :showColorSwatches="showColorSwatches"
            :colors="availableColors"
        />

        <!-- Accordion Category List -->
        <div
            v-if="!showSubMenu && !disableAccordions"
            class="cz2__scrollbar-hide selection-category-list cz2__steps-options__list-wrapper"
        >
            <swiper
                topQuerySelector=".selection-card"
                selectedQuerySelector=".selection-card--selected"
            >
                <!-- Show lashes as part of hair & hats step -->
                <lashes-options
                    v-if="isHairStep && !isBeardStep"
                    class="cz2__hair-step-lashes"
                />

                <accordion
                    v-for="(category, index) in categoryComponentMap"
                    :key="index"
                    :showAccordionToggle="showAccordionToggle"
                    :accordionHeading="getAccordionHeading(category)"
                    :quantity="category.length"
                    :index="index.toString()"
                    :class="{ 'cz2__mt-24': Object.keys(categoryComponentMap).length > 1 }"
                >
                    <template v-slot:body>
                        <div
                            v-if="hasFifaStuff(category)"
                            class="cz2__category-subheading-wrapper cz2__ml-layout"
                        >
                            <button
                                type="button"
                                @click="showSpecialBoxModal"
                                :aria-label="$t('SPECIAL_BOX_INFO_ARIA')"
                            >
                                <img
                                    src="../assets/icons/information-icon-white.svg"
                                    width="15"
                                    height="15"
                                >
                            </button>

                            <p>
                                {{ specialBoxMessage }}
                            </p>
                        </div>

                        <div
                            v-else-if="isAccessoriesStep && firstAccessoryZeroPricePerBuildEnabled"
                            class="cz2__category-subheading-wrapper cz2__ml-layout"
                        >
                            <p>
                                {{ getSubheading(category) }}
                            </p>
                        </div>

                        <price-disclaimer v-if="getComponentPrice(getSecondishComponent(category)) !== ''" />

                        <selection-card-list>
                            <selection-card
                                v-for="component in category"
                                :key="component.code"
                                :code="component.code"
                                :count="getVariantCount(component)"
                                :image="getImage(component)"
                                :exactImage="getIfImageExact(component)"
                                :allowBranded="!showSubMenu"
                                :isSubMenu="showSubMenu"
                                :isPoster="isPoster(component)"
                                :name="component.description[0].name"
                                :component="component"
                                @select="checkIfColorAvailable(component, false)"
                                :isSelected="isSelected(component)"
                                :isUnavailable="isUnavailable(component)"
                                :isOutOfStock="isOutOfStock(component)"
                                :disabled="isOutOfStock(component) && !isTotallyOutOfStock(component)"
                            />
                        </selection-card-list>
                    </template>
                </accordion>
            </swiper>
        </div>

        <!-- Plain List -->
        <div
            v-if="disableAccordions && !showSubMenu"
            class="cz2__scrollbar-hide selection-category-list cz2__steps-options__list-wrapper"
        >
            <swiper
                topQuerySelector=".selection-card"
                selectedQuerySelector=".selection-card--selected"
            >
                <selection-card-list>
                    <selection-card
                        v-for="component in filteredComponents"
                        :key="component.code"
                        :code="component.code"
                        :count="getVariantCount(component)"
                        :image="getImage(component)"
                        :exactImage="getIfImageExact(component)"
                        :name="component.description[0].name"
                        :component="component"
                        :allowBranded="!showSubMenu"
                        :isSubMenu="showSubMenu"
                        :isSelected="isSelected(component)"
                        :isUnavailable="isUnavailable(component)"
                        :isOutOfStock="isOutOfStock(component)"
                        :disabled="isOutOfStock(component) && !isTotallyOutOfStock(component)"
                        @select="checkIfColorAvailable(component, false)"
                    />
                </selection-card-list>
            </swiper>
        </div>

        <!-- Color Sub-Menu -->
        <div
            v-if="showSubMenu"
            class="cz2__scrollbar-hide selection-category-list cz2__steps-options__list-wrapper"
        >
            <swiper
                topQuerySelector=".selection-card"
                selectedQuerySelector=".selection-card--selected"
            >
                <selection-card-list>
                    <selection-card
                        v-for="color in colors"
                        :key="color.code"
                        :image="''"
                        :allowBranded="!showSubMenu"
                        :isSubMenu="showSubMenu"
                        :name="color.code"
                        :code="color.code"
                        :component="color.component"
                        :isSelected="selectedComponentColor && selectedComponentColor.code === color.code"
                        :isOutOfStock="isOutOfStock(color.component)"
                        @select="selectComponentColor(color)"
                        :disabled="isOutOfStock(color.component)"
                    />
                </selection-card-list>
            </swiper>
        </div>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__steps-option {
            pointer-events: auto;

            .cz2__hair-step-lashes {
                margin: 24px var(--layout-spacing);
                padding-bottom: 24px;
                border-bottom: 1.5px solid rgba(255, 255, 255, .32);
            }

            .selection-category-list {
                overflow: auto;

                display: flex;
                flex-direction: column;
                gap: var(--layout-gap);

                .cz2__category-subheading-wrapper {
                    margin-top: 24px;
                    color: white;
                    font-family: 'Open Sans', sans-serif;
                    font-size: 14px;
                    font-weight: 400;
                    display: flex;
                    align-items: flex-start;

                    button {
                        margin-right: 4px;
                    }
                }

                .cz2__disclaimer {
                    margin: 16px 0 -8px var(--layout-spacing);
                }
            }
        }

        &.cz2__is-kiosk {
            .cz2__steps-option .selection-category-list .cz2__category-subheading-wrapper {
                padding-left: 16px;
                font-size: 18px;

                img {
                    width: 20px;
                    height: 20px;
                }
            }

            .cz2__hair-step-lashes {
                margin-left: calc(2 * var(--layout-spacing));
                margin-right: 98px;
            }
        }
    }
</style>

<script>
    import * as constants from '../constants';

    import Swiper from './ui/Swiper.vue';
    import placeholderIcon from '../assets/icons/placeholder-selection-icon.svg';

    import Accordion from './ui/Accordion.vue';
    import StepHeader from './ui/StepHeader.vue';
    import SelectionCard from './ui/SelectionCard.vue';
    import SelectionCardList from './ui/SelectionCardList.vue';
    import PriceDisclaimer from './PriceDisclaimer.vue';
    import LashesOptions from './LashesOptions.vue';
    import { PLACEMENT_BODY } from '../constants';

    import { formatPrice } from '../helpers/formatPrice';
    import { isOutOfStockCached } from '../helpers/outofstock';

    import { getPrice } from '../helpers/productData';

    export default {
        components: {
            Accordion,
            StepHeader,
            SelectionCard,
            SelectionCardList,
            Swiper,
            PriceDisclaimer,
            LashesOptions,
        },

        props: {
            hidePlaceholders: {
                type: Boolean,
                default: false,
            },
            disableAccordions: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                displaySubMenuTitle: false,
                clickedComponent: null,
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            mainController() {
                return this.$store.state.customizer.mainController;
            },

            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            options() {
                return this.$store.state.customizer.options;
            },

            canBuyPop() {
                return this.options && this.options.canBuyPop;
            },

            editPlacement() {
                if (this.viewController == null) {
                    return null;
                }

                if (this.currentGroup.placements == null || this.currentGroup.placements.length === 0) {
                    return null;
                }

                return this.currentGroup.placements[0];
            },

            allComponents() {
                let components;

                if (this.hidePlaceholders) {
                    components = this.editPlacement.components.filter((c) => !c.placeholder && this.viewController.isAllowed(c));
                } else {
                    components = this.editPlacement.components.filter((c) => this.viewController.isAllowed(c));
                }

                // Hide... hidden...
                components = components.filter((x) => x.custom['fun-hidden'] !== 'true');

                return components;
            },

            isBaby() {
                return this.$store.getters.isBaby;
            },

            availableComponents() {
                const placement = this.editPlacement;

                if (placement == null) {
                    return [];
                }

                let components;

                if (this.hidePlaceholders) {
                    components = placement.components.filter((c) => !c.placeholder && this.viewController.isAllowed(c));
                } else {
                    components = placement.components.filter((c) => this.viewController.isAllowed(c));
                }

                // Hide... hidden...
                components = components.filter((x) => x.custom['fun-hidden'] !== 'true');

                if (placement.custom && placement.custom['fun-sort'] === 'category') {
                    components.sort((a, b) => {
                        const aSort = this.getCategoryName(a);
                        const bSort = this.getCategoryName(b);

                        let aS;
                        if (a.placeholder) {
                            aS = '__';
                        } else if (aSort === 'Bestsellers') {
                            aS = '_Bestsellers';
                        } else {
                            aS = aSort;
                        }

                        if (this.isBaby && a.custom.tags && a.custom.tags.includes('accessory-baby')) {
                            aS = '_Baby';
                        }

                        let bS;
                        if (b.placeholder) {
                            bS = '__';
                        } else if (bSort === 'Bestsellers') {
                            bS = '_Bestsellers';
                        } else {
                            bS = bSort;
                        }

                        if (this.isBaby && b.custom.tags && b.custom.tags.includes('accessory-baby')) {
                            bS = '_Baby';
                        }

                        if (aS < bS) {
                            return -1;
                        }

                        if (aS > bS) {
                            return 1;
                        }

                        return 0;
                    });
                }

                return components;
            },

            /**
             * Get a map of the style to a list of components with that style
             */
            styleComponentsMap() {
                const components = this.allComponents;

                const styleOccurrences = {};
                const tempGrouping = {};
                const finalOutput = {};

                components.forEach((c) => { // Count occurrences and group components by style
                    if (this.viewController.isAllowed(c)) {
                        const style = this.getComponentStyle(c);
                        styleOccurrences[style] = (styleOccurrences[style] || 0) + 1;

                        if (!tempGrouping[style]) {
                            tempGrouping[style] = [];
                        }
                        tempGrouping[style].push(c);
                    }
                });

                // Sort each style.
                // eslint-disable-next-line no-restricted-syntax
                for (const groupingKey of Object.keys(tempGrouping)) {
                    const grouping = tempGrouping[groupingKey];

                    grouping.sort(this.sortStyle);
                }

                components.forEach((c, index) => {
                    if (this.viewController.isAllowed(c)) {
                        const style = this.getComponentStyle(c);
                        const total = styleOccurrences[style];

                        // Group hairstyles and Beards even if minimum isn't met
                        if ((constants.HAIR_STEPS.includes(this.currentGroup.code) && total >= 2) || total >= this.getStackingMinimum(c)) {
                            // eslint-disable-next-line
                            if (!finalOutput.hasOwnProperty(style)) {
                                finalOutput[style] = this.sortOutOfStock(tempGrouping[style]);
                            }
                        } else {
                            const uniqueKey = `singular_${index}`;
                            finalOutput[uniqueKey] = [c];
                        }
                    }
                });

                return finalOutput;
            },

            /**
             * Get a component for each style, defaulting to the first available color
             */
            filteredComponents() {
                const {
                    styleComponentsMap,
                } = this;
                const componentForEachStyle = [];

                Object.keys(styleComponentsMap).forEach((style) => {
                    // If there is a selected component and it has this style, use that component instead
                    if (this.selectedComponent && this.getComponentStyle(this.selectedComponent) === style) {
                        componentForEachStyle.push(this.selectedComponent);
                        return;
                    }

                    const componentsForStyle = styleComponentsMap[style];
                    const match = componentsForStyle.find(
                        (c) => c.custom['fun-classification'] !== 'hat' && c.custom['fun-color'] === this.selectedSwatchColor,
                    );
                    let componentForStyle = componentsForStyle ? componentsForStyle[0] : null;

                    if (match) {
                        componentForStyle = match;
                    }

                    componentForEachStyle.push(componentForStyle);
                });

                const filtered = componentForEachStyle.filter((c) => c !== null);

                return this.sortOutOfStock(filtered);
            },

            selectedComponent() {
                const placement = this.editPlacement;

                if (placement == null) {
                    return null;
                }

                return this.viewController.selectedAtPlacement(placement.code);
            },

            selectedComponentColor() {
                return this.selectedComponent ? this.selectedComponent : null;
            },

            selectedSwatchColor() {
                return this.$store.state.customizer.selectedSwatchColors[this.currentGroup.name];
            },

            colors() {
                const c = this.clickedComponent !== null && this.isTotallyOutOfStock(this.clickedComponent) ? this.clickedComponent : this.selectedComponent;

                if (c && !c.placeholder) {
                    return this.getAvailableColorsForComponent(c);
                }

                return null;
            },

            /**
             * Get a list of all available colors
             */
            availableColors() {
                const filteredColors = this.availableComponents.filter(
                    (component) => !component.placeholder && component.custom?.['fun-classification'] !== 'hat',
                );
                const allColors = [...new Map(filteredColors.map((component) => [component.custom['fun-color'] || null, component])).values()];
                const mappedComponents = allColors.map((component) => ({
                    color: component.custom['fun-color'],
                }));

                return mappedComponents;
            },

            previousGroup() {
                return this.$store.getters.previousGroup;
            },

            nextGroup() {
                return this.$store.getters.nextGroup;
            },

            isOptional() {
                if (this.currentGroup == null) {
                    return false;
                }
                const isGroupOptional = this.currentGroup.custom && this.currentGroup.custom['fun-optional'] === 'true';
                return isGroupOptional;
            },

            categoryComponentMap() {
                const components = this.filteredComponents;
                const categoryMap = {};
                let categoryOrder = [];

                if (this.currentGroup.code === 'Hair' && this.isFemale) {
                    categoryOrder = [
                        'Long Hair',
                        'Short Hair',
                        'Hats',
                    ];
                } else if (this.currentGroup.code === 'Hair' && !this.isFemale) {
                    categoryOrder = [
                        'Short Hair',
                        'Long Hair',
                        'Hats',
                    ];
                } else if (this.viewController.blueprint.custom['fun-category-order']) {
                    categoryOrder = this.viewController.blueprint.custom['fun-category-order'].split(',').map((x) => x.trim()).filter((x) => x.length > 0);
                }

                categoryOrder.forEach((categoryName) => {
                    if (categoryName === 'Babies' && !this.isBaby) {
                        return;
                    }

                    categoryMap[categoryName] = [];
                });

                const placeholders = [];

                components.forEach((c) => {
                    if (c.placeholder) {
                        placeholders.push(c);
                        return;
                    }

                    const category = this.getCategoryName(c) || 'Uncategorized';

                    if (!categoryMap[category]) {
                        categoryMap[category] = [];
                    }

                    categoryMap[category].push(c);
                });

                // Delete any empty categories
                Object.keys(categoryMap).forEach((category) => {
                    if (categoryMap[category].length === 0) {
                        delete categoryMap[category];
                    }
                });

                // Add placeholder(s) to first group if both exist
                if (Object.keys(categoryMap).length && placeholders.length) {
                    // Add placeholder component to the first key group
                    const firstKey = Object.keys(categoryMap)[0];

                    categoryMap[firstKey] = [
                        ...placeholders,
                        ...categoryMap[firstKey],
                    ];
                }

                return categoryMap;
            },

            hasColors() {
                return this.colors && this.colors.length > 1;
            },

            selectedComponentHasSubMenu() {
                // Is the stacking allowed for component and/or group?
                const groupHasSubMenu = this.currentGroup.custom?.['fun-color-sub-menu'] === 'true';

                const componentHasSubMenu = this.selectedComponent?.custom?.['fun-color-sub-menu'] === 'true';

                if (groupHasSubMenu || componentHasSubMenu) {
                    let stackMinimum = 4;

                    if (this.selectedComponent.custom?.['fun-stack-minimum']) {
                        stackMinimum = Number(this.selectedComponent.custom?.['fun-stack-minimum']);
                    }

                    return stackMinimum >= 1 && this.hasColors;
                }

                return false;
            },

            subMenuTitle() {
                const groupSubTitle = this.currentGroup.custom?.['fun-color-sub-menu-title'];
                const componentSubTitle = this.selectedComponent.custom?.['fun-color-sub-menu-title'] || '';
                const price = this.getComponentPrice(this.selectedComponent);
                let priceCopy = price ? ` ${price} ${this.$t('EACH')}` : '';

                if (!this.canBuyPop) {
                    priceCopy = '';
                }

                return this.selectedComponentHasSubMenu ? `${componentSubTitle}${priceCopy}` : groupSubTitle;
            },

            subMenuSubTitle() {
                if (this.selectedComponent?.custom?.['fun-color-sub-menu-sub-title']) {
                    return this.selectedComponent?.custom?.['fun-color-sub-menu-sub-title'];
                }

                return null;
            },

            showSubMenu() {
                return this.$store.state.customizer.isSubMenuOpen;
            },

            showColorSwatches() {
                return this.currentGroup.custom?.['fun-show-swatches'] === 'true';
            },

            showAccordionToggle() {
                return Object.keys(this.categoryComponentMap).length > 1;
            },

            changeWarningShown() {
                return this.$store.state.changeWarningShown;
            },

            isFemale() {
                return this.viewController.selectedAtPlacement(PLACEMENT_BODY).tags.includes('body-female');
            },

            popControllers() {
                return this.$store.getters.popControllers;
            },

            atsLocations() {
                return this.$store.getters.atsLocations;
            },

            globalOptions() {
                return this.$store.state.options;
            },

            specialBoxModalWasShown() {
                return this.$store.state.specialBoxModalWasShown;
            },

            isAccessoriesStep() {
                return this.currentGroup?.code === 'Accessories1'
                    || this.currentGroup?.code === 'Accessories2'
                    || this.currentGroup?.code === 'Accessories3';
            },

            isFirstAccessoriesStep() {
                return this.currentGroup.code === 'Accessories1';
            },

            hasAccessory() {
                // Check if an accessory has already been selected
                const accessory1 = this.viewController.selectedAtPlacement('POPAccessory1');
                const accessory2 = this.viewController.selectedAtPlacement('POPAccessory2');
                const accessory3 = this.viewController.selectedAtPlacement('POPAccessory3');
                // TODO: Exclude all licensed accessories and not just fifa.
                const hasAccessory1 = !accessory1.placeholder && !accessory1.tags.includes('wc-fifa-campaign');
                const hasAccessory2 = !accessory2.placeholder && !accessory2.tags.includes('wc-fifa-campaign');
                const hasAccessory3 = !accessory3.placeholder && !accessory3.tags.includes('wc-fifa-campaign');

                return hasAccessory1 || hasAccessory2 || hasAccessory3;
            },

            hasLicensedAccessory() {
                // Check if an accessory has already been selected
                const accessory1 = this.viewController.selectedAtPlacement('POPAccessory1');
                const accessory2 = this.viewController.selectedAtPlacement('POPAccessory2');
                const accessory3 = this.viewController.selectedAtPlacement('POPAccessory3');
                // TODO: Account for all licensed accessories and not just fifa.
                const hasAccessory1 = !accessory1.placeholder && accessory1.tags.includes('wc-fifa-campaign');
                const hasAccessory2 = !accessory2.placeholder && accessory2.tags.includes('wc-fifa-campaign');
                const hasAccessory3 = !accessory3.placeholder && accessory3.tags.includes('wc-fifa-campaign');

                return hasAccessory1 || hasAccessory2 || hasAccessory3;
            },

            isHairStep() {
                return constants.HAIR_STEPS.includes(this.currentGroup.code);
            },

            isBeardStep() {
                return this.currentGroup.code === 'Beard';
            },

            specialBoxMessage() {
                return this.$t('SPECIAL_BOX_MESSAGE').replace('{0}', this.fifaPrice);
            },

            fifaPrice() {
                const component = this.viewController.findPlacement('POPFifaBundle')?.components?.find((x) => !x.placeholder);

                if (component) {
                    const priceValue = this.viewController.getPrice(component);

                    return `${formatPrice(this.$store.state, priceValue, 'compact')} `;
                }

                return 0;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            firstAccessoryZeroPricePerBuildEnabled() {
                return this.$store.state.ecommerce.firstAccessoryZeroPricePerBuildEnabled;
            },
        },

        mounted() {
            this.updateNavigation();

            // If the selected component is a placeholder, wipe out the selected swatch color.
            // This may happen when the previously selected option got reset by changes in some other group.
            if (this.selectedComponent?.placeholder) {
                this.setSelectedSwatch(null);
            }
        },

        watch: {
            selectedComponent() {
                this.updateNavigation();
            },

            selectedSwatchColor() {
                // Check color availability
                this.checkIfColorAvailable(this.selectedComponent, true);
            },

            showSubMenu(newValue) {
                if (newValue) {
                    // Prevent the sub-menu title from displaying before the spring transition
                    setTimeout(() => {
                        this.displaySubMenuTitle = true;
                    }, 160);
                } else {
                    this.displaySubMenuTitle = false;
                    this.clickedComponent = null;
                }
            },
        },

        methods: {
            getComponentStyle(component) {
                if (!component || component.placeholder) {
                    return null;
                }

                return component.custom?.['fun-style'] || component.code;
            },

            getStackingMinimum(component) {
                if (constants.HAIR_STEPS.includes(this.currentGroup.code)) {
                    return 2;
                }

                const groupHasSubMenu = this.currentGroup.custom?.['fun-color-sub-menu'] === 'true';

                const componentHasSubMenu = component?.custom?.['fun-color-sub-menu'] === 'true';

                if (groupHasSubMenu || componentHasSubMenu) {
                    if (component.custom?.['fun-stack-minimum']) {
                        return +component.custom['fun-stack-minimum'];
                    }

                    return 4;
                }

                return 1000;
            },

            /**
             * Get the number of products that have the same style as the given component
             */
            getVariantCount(component) {
                if (!component || component.placeholder) {
                    return -1;
                }

                const style = this.getComponentStyle(component);
                const styleMap = this.styleComponentsMap;

                if (Array.isArray(styleMap[style])) {
                    const styles = this.isTotallyOutOfStock(component) ? styleMap[style] : styleMap[style].filter((c) => !this.isOutOfStock(c));

                    return styles.length;
                }

                return -1;
            },

            getImage(component) {
                // If a selected component is not the current image...
                if (this.selectedComponent?.code !== component.code) {
                    // Should we use posters for the >current< component instead?
                    if (component.custom['fun-poster-image']) {
                        return component.custom['fun-poster-image'];
                    }
                }

                return component.placeholder ? placeholderIcon : component.custom.image;
            },

            isPoster(component) {
                // If a selected component is not the current image...
                if (this.selectedComponent?.code !== component.code) {
                    // Should we use posters for the >current< component instead?
                    if (component.custom['fun-poster-image']) {
                        return true;
                    }
                }

                return false;
            },

            getIfImageExact(component) {
                // If a selected component is not the current image...
                if (this.selectedComponent?.code !== component.code) {
                    // Should we use posters for the >current< component instead?
                    if (component.custom['fun-poster-image']) {
                        return true;
                    }
                }

                return false;
            },

            getComponentsThatMatch(component) {
                if (!component || component.placeholder) {
                    return [];
                }

                const styleType = this.getComponentStyle(component);
                const allStylesThatMatch = this.availableComponents.filter((c) => this.getComponentStyle(c) === styleType);

                if (allStylesThatMatch.length < this.getStackingMinimum(component)) {
                    return [component];
                }

                allStylesThatMatch.sort(this.sortStyle);

                return this.sortOutOfStock(allStylesThatMatch);
            },

            /**
             * Get the colors for the components that match selected
             */
            getAvailableColorsForComponent(component) {
                const components = this.getComponentsThatMatch(component).map((c) => ({
                    color: c.custom['fun-color'] || 'N/A',
                    placementIndependentCode: c.placementIndependentCode,
                    code: c.code,
                    name: c.description[0].name,
                    component: c,
                    // 'custom' structure to make the sorter reusable.
                    custom: {
                        'fun-style-order': c.custom?.['fun-style-order'],
                    },
                }));

                return components;
            },

            select(component, triggeredByColorSelection) {
                const placement = this.editPlacement;
                const {
                    viewController,
                } = this;

                if (placement === null || viewController === null) {
                    return;
                }

                if (!this.isSelected(component)) {
                    this.$store.dispatch('updateComponent', {
                        placement: placement.code,
                        code: component.code,
                    }).then(() => {
                        if (!triggeredByColorSelection) {
                            this.openSubMenu();
                        }

                        this.triggerModal(component);
                    }).catch(() => {
                    });

                    if (!this.selectedComponentHasSubMenu) {
                        this.setColorUnavailableStates();
                    }
                }

                if (!triggeredByColorSelection) {
                    this.openSubMenu();
                }

                this.updateNavigation();
            },

            /**
             * If there's a selected component, we want to update the component the same style option, but with the new color
             */
            selectComponentColor(color) {
                if (this.selectedComponent && !this.selectedComponent.placeholder) {
                    const componentStyle = this.getComponentStyle(this.selectedComponent);

                    const matchingComponent = this.styleComponentsMap[componentStyle]
                        .find((c) => c.placementIndependentCode === color.placementIndependentCode);

                    this.select(matchingComponent);
                }
            },

            isSelected(component) {
                return this.selectedComponent && this.selectedComponent.code === component.code;
            },

            isUnavailable(component) {
                if (!this.showColorSwatches || component.custom?.['fun-classification'] === 'hat') {
                    return false;
                }

                const isPlaceholder = component.placeholder;
                const availableColors = this.getAvailableColorsForComponent(component);
                const match = availableColors.find((item) => item.color === this.selectedSwatchColor);

                return this.selectedSwatchColor && !isPlaceholder && !match;
            },

            isOutOfStock(component) {
                return isOutOfStockCached(this.atsLocations, this.viewController, component);
            },

            // Used to open sub-menus anyway when all color options for a style are TOOS
            isTotallyOutOfStock(component) {
                const hasMultipleColors = this.getAvailableColorsForComponent(component).length > 1;

                return this.isOutOfStock(component) && this.selectedComponentHasSubMenu && hasMultipleColors
                    && !this.isUnavailable(component) && !component.placeholder;
            },

            checkIfColorAvailable(component, triggeredByColorSelection) {
                if (!component.placeholder && component.custom?.['fun-classification'] !== 'hat') {
                    if (this.isTotallyOutOfStock(component)) {
                        this.clickedComponent = component;
                        this.openSubMenu();

                        return;
                    }

                    const availableColors = this.getAvailableColorsForComponent(component);
                    const match = availableColors.find((item) => item.color === this.selectedSwatchColor);

                    if (match) {
                        this.select(match);
                        this.setSelectedSwatch(match.color);
                    } else if (!this.selectedSwatchColor) {
                        // if no match is found and a swatch hasn't been selected yet, default to first available color
                        this.select(availableColors[0]);
                        this.setSelectedSwatch(availableColors[0].color);
                    } else if (!this.isHairStep) {
                        this.select(availableColors[0]);

                        if (!triggeredByColorSelection) {
                            this.setSelectedSwatch(availableColors[0].color);
                        }
                    }
                } else {
                    this.select(component, triggeredByColorSelection);
                }

                this.clickedComponent = null;
            },

            setSelectedSwatch(color) {
                this.$store.dispatch('setSelectedSwatchColors', color);
            },

            setColorUnavailableStates() {
                if (this.selectedComponent.custom?.['fun-classification'] !== 'hat') {
                    const availableColorsForComponent = this.getAvailableColorsForComponent(this.selectedComponent);

                    this.availableColors.forEach((color) => {
                        // check for colors in availableColorsForComponent
                        const match = availableColorsForComponent.find((item) => item.color === color.color);

                        if (match) {
                            color.isUnavailable = false;
                        } else if (!this.selectedComponent.placeholder) {
                            color.isUnavailable = true;
                        }
                    });
                }
            },

            openSubMenu() {
                if (this.selectedComponentHasSubMenu && !this.changeWarningShown) {
                    this.$store.dispatch('setIsSubMenuOpen', true);
                }
            },

            updateNavigation() {
                this.$emit('updateNavigation', {
                    hasBack: !!this.previousGroup,
                    canGoBack: !!this.previousGroup || this.showToneMenu,
                    hasNext: !!this.nextGroup,
                    canGoNext: this.isOptional || (this.selectedComponent != null && !this.selectedComponent.placeholder),
                });
            },

            getCategoryName(option) {
                if (option.custom == null) {
                    return null;
                }

                if (option.custom['fun-featured'] === 'true' || option.custom['fun-featured-default'] === 'true') {
                    return this.$t('FEATURED');
                }

                return option.custom['fun-category'];
            },

            getSecondishComponent(category) {
                // First try to find _unselected_ component.
                // This is because of pricing rules on accessories.
                if (category) {
                    // eslint-disable-next-line no-unreachable-loop
                    for (let i = 1; i < category.length; i += 1) {
                        const c = category[i];

                        if (this.selectedComponent && this.selectedComponent.code === c.code) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }

                        return c;
                    }
                }

                return category[1] || category[0];
            },

            getComponentPrice(component) {
                let categoryPrice = '';

                if (component?.custom['fun-category-price'] === 'true') {
                    const price = this.viewController.getPrice(component);

                    if (price != null && price !== 0) {
                        const priceFormatted = formatPrice(this.$store.state, price, 'compact');

                        categoryPrice = `${priceFormatted} `;
                    }
                }

                return categoryPrice;
            },

            getAccordionHeading(category) {
                const secondishComponent = this.getSecondishComponent(category);
                const hasFifaStuff = this.hasFifaStuff(category);
                const categoryName = this.getCategoryName(secondishComponent);
                const categoryPrice = this.viewController.getPrice(secondishComponent);
                let priceCopy;

                if (this.isAccessoriesStep) {
                    priceCopy = hasFifaStuff ? this.fifaPrice : this.getAccessoriesPriceCopy(secondishComponent);
                } else {
                    priceCopy = this.getPriceCopy(categoryPrice, secondishComponent);
                }

                if (this.canBuyPop) {
                    return `${categoryName} ${priceCopy}(${category.length})`;
                }

                return categoryName;
            },

            getSubheading(category) {
                const secondishComponent = this.getSecondishComponent(category);

                // Looks like we can't use controller price because we need to get a "true" price.
                // const categoryPrice = this.viewController.getPrice(secondishComponent);

                const categoryPrice = getPrice(this.$store.state.ecommerce, secondishComponent, this.viewController, {
                    truePrice: true,
                });

                const priceCopy = formatPrice(this.$store.state, categoryPrice, 'compact');
                let subheading = this.$t('ACCESSORY_FREE').replace('{0}', priceCopy);

                // Only update copy if not on the first accessories step.
                if (!this.isFirstAccessoriesStep) {
                    if (this.hasAccessory) {
                        subheading = this.$t('ACCESSORY_CHARGE').replace('{0}', priceCopy);
                    } else if (this.hasLicensedAccessory) {
                        subheading = this.$t('ACCESSORY_INCLUDED');
                    }
                }

                return subheading;
            },

            getPriceCopy(categoryPrice, secondishComponent) {
                const hasPrice = categoryPrice || secondishComponent?.custom?.['fun-category-price-in-header'] === 'true';
                const price = hasPrice ? this.getCurrency(formatPrice(this.$store.state, categoryPrice, 'compact')) : '';
                const hasSecondAccessoryCharge = this.hasAccessory && this.firstAccessoryZeroPricePerBuildEnabled && !this.isFirstAccessoriesStep;
                const priceCopy = (!this.isAccessoriesStep || hasSecondAccessoryCharge) ? price : ` - ${this.$t('ITEM_INCLUDED')} `;

                return priceCopy;
            },

            getAccessoriesPriceCopy(secondishComponent) {
                const categoryPrice = getPrice(this.$store.state.ecommerce, secondishComponent, this.viewController, {
                    truePrice: true,
                });
                const price = this.getCurrency(formatPrice(this.$store.state, categoryPrice, 'compact'));
                const hasSecondAccessoryCharge = this.hasAccessory && this.firstAccessoryZeroPricePerBuildEnabled && !this.isFirstAccessoriesStep;
                let priceCopy = ` - ${this.$t('ITEM_INCLUDED')} `;

                if (categoryPrice > 0) {
                    // SFCC flag enabled and pricing is greater than $0 = show first accessory as included, show price for second accessory
                    if (this.firstAccessoryZeroPricePerBuildEnabled) {
                        if (hasSecondAccessoryCharge) {
                            priceCopy = price;
                        }
                    } else {
                        // SFCC flag disabled and pricing is greater than $0 = show accessories as priced
                        priceCopy = price;
                    }
                }

                return priceCopy;
            },

            sortOutOfStock(components) {
                // Don't sort hair options since they don't have sub-menus
                if (components[0].custom['fun-classification'] === 'hair' || components[0].custom['fun-classification'] === 'beard') {
                    return components;
                }

                const inStockComponents = components.filter((c) => !this.isOutOfStock(c));
                const oosComponents = components.filter((c) => this.isOutOfStock(c));

                return inStockComponents.concat(oosComponents);
            },

            /**
             * Sorts components within the style.
             */
            sortStyle(a, b) {
                const aOrder = a.custom?.['fun-style-order'];
                const bOrder = b.custom?.['fun-style-order'];

                // If both values are empty, use the original order.
                if (!aOrder && !bOrder) {
                    return 0;
                }

                // If only a value is empty, move it to the end.
                if (!aOrder) {
                    return 1;
                }

                // If only b value is empty, move it to the end.
                if (!bOrder) {
                    return -1;
                }

                return (+aOrder) - (+bOrder);
            },

            triggerModal(component) {
                if (component?.component?.tags?.includes('fifa-show-box-modal')) {
                    if (!this.specialBoxModalWasShown && this.isAccessoriesStep) {
                        this.$store.dispatch('showSpecialBoxModal', true);
                        this.$store.dispatch('setSpecialBoxModalWasShown', true);
                    }
                }
            },

            showSpecialBoxModal() {
                this.$store.dispatch('showSpecialBoxModal', true);
            },

            hasFifaStuff(category) {
                const hasFifaStuff = category.filter((x) => x.tags.includes('wc-fifa-campaign'));

                return hasFifaStuff.length;
            },

            getCurrency(price) {
                const currency = this.$te('PLUS_CURRENCY') ? this.$t('PLUS_CURRENCY') : '';

                if (currency) {
                    return `+ ${price} `;
                }

                return `+ ${price} USD `;
            },
        },
    };
</script>
